document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
  const slides = Array.from(gallery.querySelectorAll('[data-product-gallery-slide]'));
  const dots = Array.from(gallery.querySelectorAll('[data-product-gallery-dot]'));
  const previous = gallery.querySelector('[data-product-gallery-previous]');
  const next = gallery.querySelector('[data-product-gallery-next]');
  const status = gallery.querySelector('[data-product-gallery-status]');
  const zoom = gallery.querySelector('[data-product-gallery-zoom]');
  const lightbox = gallery.querySelector('[data-product-gallery-lightbox]');
  const lightboxViewport = gallery.querySelector('.product-gallery-lightbox__viewport');
  let lightboxImage = gallery.querySelector('[data-product-gallery-lightbox-image]');
  let lightboxBuffer = gallery.querySelector('[data-product-gallery-lightbox-buffer]');
  const lightboxClose = gallery.querySelector('[data-product-gallery-lightbox-close]');
  const lightboxPrevious = gallery.querySelector('[data-product-gallery-lightbox-previous]');
  const lightboxNext = gallery.querySelector('[data-product-gallery-lightbox-next]');
  const lightboxDots = Array.from(gallery.querySelectorAll('[data-product-gallery-lightbox-dot]'));
  const zoomableIndexes = slides.reduce((indexes, slide, index) => {
    if (slide.dataset.productGalleryZoomSrc) indexes.push(index);
    return indexes;
  }, []);
  let activeIndex = 0;
  let requestedIndex = 0;
  let navigationRequest = 0;
  let pointerStart = null;
  let activeTransition = null;
  let lightboxRenderRequest = 0;
  const zoomImageRequests = new Map();

  const isLightboxOpen = () => lightbox?.hasAttribute('open') || false;

  const activeZoomData = () => {
    const slide = slides[activeIndex];
    return {
      src: slide?.dataset.productGalleryZoomSrc || '',
      alt: slide?.dataset.productGalleryZoomAlt || ''
    };
  };

  const prepareZoomImage = (index) => {
    const src = slides[index]?.dataset.productGalleryZoomSrc || '';
    if (!src) return Promise.resolve(true);
    if (zoomImageRequests.has(src)) return zoomImageRequests.get(src);

    const request = new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';

      const finish = (isReady) => {
        image.removeEventListener('load', handleLoad);
        image.removeEventListener('error', handleError);
        resolve(isReady);
      };
      const handleLoad = () => {
        if (typeof image.decode !== 'function') {
          finish(true);
          return;
        }
        image.decode().then(() => finish(true)).catch(() => finish(image.naturalWidth > 0));
      };
      const handleError = () => finish(false);

      image.addEventListener('load', handleLoad, { once: true });
      image.addEventListener('error', handleError, { once: true });
      image.src = src;
    });

    zoomImageRequests.set(src, request);
    return request;
  };

  const syncZoomAvailability = () => {
    if (zoom) zoom.hidden = !activeZoomData().src;
  };

  const waitForPaint = () => new Promise((resolve) => {
    const timeout = window.setTimeout(resolve, 120);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.clearTimeout(timeout);
        resolve();
      });
    });
  });

  const prepareLightboxElement = (element, image) => {
    if (!element || !image.src) return Promise.resolve(false);

    element.decoding = 'async';
    element.hidden = true;
    element.classList.remove('is-active', 'is-staging');
    element.alt = image.alt;
    if (element.getAttribute('src') !== image.src) element.src = image.src;

    const decode = () => {
      if (element.naturalWidth < 1) return Promise.resolve(false);
      if (typeof element.decode !== 'function') return Promise.resolve(true);
      return element.decode().then(() => true).catch(() => element.naturalWidth > 0);
    };

    if (element.complete) return decode();

    return new Promise((resolve) => {
      const finish = () => {
        element.removeEventListener('load', finish);
        element.removeEventListener('error', finish);
        decode().then(resolve);
      };

      element.addEventListener('load', finish, { once: true });
      element.addEventListener('error', finish, { once: true });
    });
  };

  const syncLightboxState = async () => {
    if (!lightboxImage) return false;
    const image = activeZoomData();
    if (!image.src) return false;

    const renderRequest = ++lightboxRenderRequest;
    if (lightboxImage.getAttribute('src') === image.src && lightboxImage.naturalWidth > 0) {
      lightboxImage.alt = image.alt;
      lightboxImage.hidden = false;
      lightboxImage.classList.add('is-active');
    } else {
      const incoming = lightboxBuffer || lightboxImage;
      const isReady = await prepareLightboxElement(incoming, image);
      if (!isReady || renderRequest !== lightboxRenderRequest) return false;

      incoming.hidden = false;
      incoming.classList.add('is-staging');
      if (isLightboxOpen()) await waitForPaint();
      if (renderRequest !== lightboxRenderRequest) return false;

      const outgoing = lightboxImage;
      outgoing.classList.remove('is-active', 'is-staging');
      if (outgoing !== incoming) outgoing.hidden = true;
      incoming.classList.remove('is-staging');
      incoming.classList.add('is-active');

      if (lightboxBuffer) {
        lightboxImage = incoming;
        lightboxBuffer = outgoing;
      }
    }

    lightboxDots.forEach((dot) => {
      dot.setAttribute('aria-current', Number(dot.dataset.slideIndex) === activeIndex ? 'true' : 'false');
    });
    return true;
  };

  const prepareSlide = (index, priority = 'auto') => {
    const image = slides[index]?.querySelector('img');
    if (!image) return Promise.resolve(true);

    image.loading = 'eager';
    image.setAttribute('fetchpriority', priority);

    const decode = () => {
      if (image.naturalWidth < 1) return Promise.resolve(false);
      if (typeof image.decode !== 'function') return Promise.resolve(true);
      return image.decode().then(() => true).catch(() => image.naturalWidth > 0);
    };

    if (image.complete) return decode();

    return new Promise((resolve) => {
      const finish = () => {
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        decode().then(resolve);
      };

      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
    });
  };

  const warmAdjacentSlides = (index) => {
    if (slides.length < 2) return;
    prepareSlide((index + 1) % slides.length);
    prepareSlide((index - 1 + slides.length) % slides.length);
  };

  const scheduleInitialWarmup = () => {
    const warm = () => warmAdjacentSlides(activeIndex);

    prepareSlide(activeIndex, 'high').then(() => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warm, { timeout: 1200 });
        return;
      }
      window.setTimeout(warm, 200);
    });
  };

  const settleSlides = (index) => {
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.classList.remove('is-entering', 'is-leaving');
      slide.hidden = !isActive;
    });
  };

  const finishActiveTransition = () => {
    if (!activeTransition) return;
    activeTransition.finish();
  };

  const transitionTo = (index) => new Promise((resolve) => {
    if (index === activeIndex) {
      settleSlides(index);
      resolve();
      return;
    }

    finishActiveTransition();

    const outgoing = slides[activeIndex];
    const incoming = slides[index];
    if (!outgoing || !incoming) {
      activeIndex = index;
      settleSlides(activeIndex);
      resolve();
      return;
    }

    let timeout;
    let firstFrame;
    let secondFrame;
    let isFinished = false;

    const finish = () => {
      if (isFinished) return;
      isFinished = true;
      window.clearTimeout(timeout);
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      activeIndex = index;
      settleSlides(activeIndex);
      if (activeTransition?.finish === finish) activeTransition = null;
      resolve();
    };

    outgoing.hidden = false;
    outgoing.classList.add('is-active', 'is-leaving');
    incoming.hidden = false;
    incoming.classList.remove('is-active', 'is-leaving');
    incoming.classList.add('is-entering');
    activeTransition = { finish };

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(finish);
    });

    timeout = window.setTimeout(finish, 120);
  });

  const show = async (nextIndex) => {
    if (slides.length < 1) return;

    requestedIndex = (nextIndex + slides.length) % slides.length;
    const request = ++navigationRequest;
    const lightboxWasOpen = isLightboxOpen();
    if (lightboxWasOpen) lightboxRenderRequest += 1;
    gallery.setAttribute('aria-busy', 'true');
    const [slideIsReady, zoomIsReady] = await Promise.all([
      prepareSlide(requestedIndex, 'high'),
      lightboxWasOpen ? prepareZoomImage(requestedIndex) : Promise.resolve(true)
    ]);
    if (request !== navigationRequest) return;
    if (!slideIsReady || !zoomIsReady) {
      gallery.removeAttribute('aria-busy');
      return;
    }

    await transitionTo(requestedIndex);
    if (request !== navigationRequest) return;
    gallery.removeAttribute('aria-busy');
    dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    });
    if (status) {
      status.textContent = status.dataset.statusTemplate
        .replace('__current__', activeIndex + 1)
        .replace('__total__', slides.length);
    }
    syncZoomAvailability();
    if (isLightboxOpen() && !await syncLightboxState()) return;
    if (request !== navigationRequest) return;
    warmAdjacentSlides(activeIndex);
  };

  const adjacentZoomIndex = (direction) => {
    if (zoomableIndexes.length < 1) return activeIndex;
    const currentPosition = zoomableIndexes.indexOf(activeIndex);
    const position = currentPosition < 0 ? 0 : currentPosition;
    return zoomableIndexes[(position + direction + zoomableIndexes.length) % zoomableIndexes.length];
  };

  const navigateLightbox = (direction) => {
    show(adjacentZoomIndex(direction));
  };

  const cleanupLightbox = () => {
    lightboxRenderRequest += 1;
    document.documentElement.classList.remove('product-gallery-lightbox-open');
    lightbox?.removeAttribute('aria-modal');
    [lightboxImage, lightboxBuffer].forEach((image) => {
      if (!image) return;
      image.hidden = true;
      image.classList.remove('is-active', 'is-staging');
      image.removeAttribute('src');
    });
    zoom?.focus();
  };

  const closeLightbox = () => {
    if (!lightbox) return;
    if (typeof lightbox.close === 'function' && isLightboxOpen()) lightbox.close();
    else {
      lightbox.removeAttribute('open');
      cleanupLightbox();
    }
  };

  const openLightbox = async () => {
    if (!lightbox || !lightboxImage) return;
    const image = activeZoomData();
    if (!image.src) return;
    if (!await prepareZoomImage(activeIndex)) return;

    if (!await syncLightboxState()) return;
    document.documentElement.classList.add('product-gallery-lightbox-open');
    if (typeof lightbox.showModal === 'function') lightbox.showModal();
    else {
      lightbox.setAttribute('open', '');
      lightbox.setAttribute('aria-modal', 'true');
    }
    lightboxClose?.focus();
  };

  previous?.addEventListener('click', () => show(requestedIndex - 1));
  next?.addEventListener('click', () => show(requestedIndex + 1));

  dots.forEach((dot) => {
    dot.addEventListener('click', () => show(Number(dot.dataset.slideIndex)));
  });

  zoom?.addEventListener('click', openLightbox);
  lightboxClose?.addEventListener('click', closeLightbox);
  lightboxPrevious?.addEventListener('click', () => navigateLightbox(-1));
  lightboxNext?.addEventListener('click', () => navigateLightbox(1));
  lightboxDots.forEach((dot) => {
    dot.addEventListener('click', () => show(Number(dot.dataset.slideIndex)));
  });
  lightboxViewport?.addEventListener('click', (event) => {
    if (event.target === lightboxViewport) closeLightbox();
  });
  lightbox?.addEventListener('close', cleanupLightbox);
  lightbox?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeLightbox();
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      navigateLightbox(event.key === 'ArrowLeft' ? -1 : 1);
      return;
    }
    if (event.key === 'Tab' && typeof lightbox.showModal !== 'function') {
      event.preventDefault();
      const focusable = [lightboxClose, lightboxPrevious, ...lightboxDots, lightboxNext]
        .filter((control) => control && !control.hidden && !control.disabled);
      if (focusable.length < 1) return;
      const currentIndex = focusable.indexOf(document.activeElement);
      const direction = event.shiftKey ? -1 : 1;
      const nextIndex = currentIndex < 0
        ? (event.shiftKey ? focusable.length - 1 : 0)
        : (currentIndex + direction + focusable.length) % focusable.length;
      focusable[nextIndex].focus();
    }
  });

  gallery.addEventListener('keydown', (event) => {
    if (isLightboxOpen()) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    show(requestedIndex + (event.key === 'ArrowLeft' ? -1 : 1));
  });

  gallery.addEventListener('pointerdown', (event) => {
    if (isLightboxOpen()) return;
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
  }, { passive: true });

  gallery.addEventListener('pointerup', (event) => {
    if (!pointerStart || pointerStart.id !== event.pointerId) return;

    const deltaX = event.clientX - pointerStart.x;
    const deltaY = event.clientY - pointerStart.y;
    pointerStart = null;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    show(requestedIndex + (deltaX < 0 ? 1 : -1));
  }, { passive: true });

  gallery.addEventListener('pointercancel', () => {
    pointerStart = null;
  }, { passive: true });

  syncZoomAvailability();
  settleSlides(activeIndex);
  scheduleInitialWarmup();
  if (lightboxPrevious) lightboxPrevious.hidden = zoomableIndexes.length < 2;
  if (lightboxNext) lightboxNext.hidden = zoomableIndexes.length < 2;
});
